import { Component, Input, OnInit } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Practice } from '../practices/practice';
import { UpdoxUser } from './updox-user';
import { UpdoxService } from '../../../services/updox.service';
import { UserService } from '../../../services/user.service';
import { AlertService } from '../../../_alert';
import { GlobalErrorHandlerService } from '../../../services/global-error.service';
@Component({
  selector: 'app-updox-integration',
  templateUrl: './updox-integration.component.html',
})
export class UpdoxIntegrationComponent implements OnInit {
  @Input() practice: Practice;

  updoxUser: UpdoxUser = null;
  showForm = false;
  isEditing = false;
  isSaving = false;
  isLoading = false;
  directAddressDomain = environment.updox_direct_address_domain;
  private editSnapshot: UpdoxUser = null;

  constructor(
    private updoxService: UpdoxService,
    private userService: UserService,
    private alertService: AlertService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.updoxService.getUpdoxUser(this.practice.id)
      .then((resp: any[]) => {
        this.isLoading = false;
        if (resp && resp.length > 0) {
          this.updoxUser = new UpdoxUser().load_from_json(resp[0]);
        }
      })
      .catch(() => {
        this.isLoading = false;
        this.globalErrorHandler.error = 'Failed to load Updox user. Please refresh and try again.';
      });
  }

  get canManage(): boolean {
    return this.userService.current_user.isEnterprise;
  }

  private emailLocalPart(): string {
    const email = this.practice.email || this.practice.client?.email || '';
    if (email && email.includes('@')) {
      return email.split('@')[0].toLowerCase().replace(/[^a-z0-9._-]/g, '');
    }
    return `practice${this.practice.id}`;
  }

  openCreateForm(): void {
    const localPart = this.emailLocalPart();
    const domain = this.directAddressDomain;
    const currentUser = this.userService.current_user;

    this.updoxUser = new UpdoxUser();
    this.updoxUser.practice_id = this.practice.id;
    this.updoxUser.first_name = this.practice.requester_firstname || '';
    this.updoxUser.last_name = (this.practice.request_lastname as any) || '';
    this.updoxUser.updox_user_id = `ww-practice-${this.practice.id}-prod`;
    this.updoxUser.updox_username = localPart;
    this.updoxUser.direct_address_local = localPart;
    this.updoxUser.direct_address = `${localPart}@${domain}`;
    this.updoxUser.active = true;

    this.showForm = true;
    this.isEditing = false;
  }

  openEditForm(): void {
    // take snapshot so Cancel can restore original values
    this.editSnapshot = new UpdoxUser().load_from_json({
      id: this.updoxUser.id,
      updox_user_id: this.updoxUser.updox_user_id,
      first_name: this.updoxUser.first_name,
      last_name: this.updoxUser.last_name,
      updox_username: this.updoxUser.updox_username,
      updox_password: '',
      direct_address: this.updoxUser.direct_address,
      direct_address_local: this.updoxUser.direct_address_local,
      active: this.updoxUser.active,
      user_id: this.updoxUser.user_id,
      practice_id: this.updoxUser.practice_id,
    });
    this.showForm = true;
    this.isEditing = true;
  }

  cancelForm(): void {
    this.showForm = false;
    this.isEditing = false;
    if (this.editSnapshot) {
      this.updoxUser = this.editSnapshot;
      this.editSnapshot = null;
    } else if (!this.updoxUser.is_persisted) {
      this.updoxUser = null;
    }
  }

  onDirectAddressLocalChange(): void {
    const local = this.updoxUser.direct_address_local || '';
    this.updoxUser.direct_address = `${local}@${this.directAddressDomain}`;
  }

  save(): void {
    this.isSaving = true;
    const wasEditing = this.isEditing;
    const action = wasEditing
      ? this.updoxService.updateUser(this.updoxUser)
      : this.updoxService.saveUser(this.updoxUser);

    action
      .then(resp => {
        this.isSaving = false;
        const updoxResponse = resp?.updox_response;
        if (updoxResponse && !updoxResponse.successful) {
          this.globalErrorHandler.error = updoxResponse.responseMessage || 'An error occurred. Please try again.';
          return;
        }
        const userData = resp?.updox_user || resp;
        if (!userData || !userData.id) {
          this.globalErrorHandler.error = 'Unexpected response from server. Please try again.';
          return;
        }
        this.updoxUser = new UpdoxUser().load_from_json(userData);
        this.editSnapshot = null;
        this.showForm = false;
        this.isEditing = false;
        const msg = wasEditing ? 'Updox user updated successfully.' : 'Updox user created successfully.';
        this.alertService.success(msg, this.alertService.options);
      })
      .catch(resp => {
        this.isSaving = false;
        const body = resp?.error;
        this.globalErrorHandler.error =
          body?.responseMessage || body?.error || 'An error occurred. Please try again.';
      });
  }
}
